import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { makeSentryIgnoredError } from '~/components/ErrorBoundaryPage';
import { useOverlayContext, useStoreActions } from '~/contexts';
import { useLegalTerms } from '~/hooks/reactQuery/queries/useLegalTerms';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { useRedirectToProposal } from '../useCreateAgreement';
import { mapAgreementToDraft } from './mapAgreementToDraft';
export const useDuplicateRelationship = ()=>{
    const { api } = useStoreActions();
    const { startLoader, stopLoader, showErrorToast } = useOverlayContext();
    const { openCreationModal } = useRedirectToProposal();
    const { CUSTOM_PAGES } = useFeatureFlags();
    const { t: dupTexts } = useLocaleKeys('hooks.useDuplicateRelationship');
    const { refresh: refreshLegalTerms } = useLegalTerms();
    async function fetchLegalTermsDocIds() {
        const result = await refreshLegalTerms({
            throwOnError: false
        });
        if (result.error || !result.data) {
            throw makeSentryIgnoredError('legalTermsError');
        }
        return result.data.map((doc)=>doc.id);
    }
    async function loadAndGo(type, action) {
        let opts = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
        try {
            startLoader("duplicate-".concat(type));
            return Promise.resolve(await action());
        } catch (e) {
            if (opts.onError) {
                opts.onError(e);
            } else {
                showErrorToast(dupTexts.failedToDuplicate({
                    type
                }));
            }
            return Promise.reject();
        } finally{
            stopLoader("duplicate-".concat(type));
        }
    }
    async function getRelationship(relationshipId) {
        return await api.relationship.get(relationshipId);
    }
    async function duplicateRelationshipAndRedirect(relationshipId) {
        let strategy = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 'duplicate-agreement';
        openCreationModal({
            draftIdGetter: async ()=>{
                return loadAndGo('agreement', async ()=>{
                    const [currentAgreement, legalTermsDocIds] = await Promise.all([
                        getRelationship(relationshipId),
                        fetchLegalTermsDocIds()
                    ]);
                    if (currentAgreement != null) {
                        const draftData = mapAgreementToDraft(currentAgreement, {
                            strategy,
                            legalTermsDocIds,
                            customPagesFFEnabled: !!CUSTOM_PAGES
                        });
                        await api.draft.save(draftData);
                        return draftData.id;
                    } else {
                        return Promise.reject(dupTexts.errorFetchingAgreement());
                    }
                });
            }
        });
        // openCreationModal handles the redirect; preserve the void return for the modal path.
        return Promise.resolve();
    }
    const createStandaloneDraftFromAgreement = async function createStandaloneDraftFromAgreement(relationshipId) {
        let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        const { strategy = 'duplicate-agreement' } = opts;
        const [currentAgreement, legalTermsDocIds] = await Promise.all([
            getRelationship(relationshipId),
            fetchLegalTermsDocIds()
        ]);
        const draftData = mapAgreementToDraft(currentAgreement, {
            strategy,
            legalTermsDocIds,
            customPagesFFEnabled: !!CUSTOM_PAGES
        });
        await api.draft.save(draftData);
        return draftData.id;
    };
    async function duplicateRelationship(relationshipId) {
        let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
        return loadAndGo('agreement', async ()=>createStandaloneDraftFromAgreement(relationshipId, opts), opts);
    }
    async function duplicateDraftFromId(draftId) {
        openCreationModal({
            draftIdGetter: async ()=>{
                return loadAndGo('draft', async ()=>{
                    const res = await api.draft.getById(draftId);
                    const id = generateUniqueId('draft');
                    const draftToSave = res.setEffectiveDate(null).setId(id).toDTO();
                    await api.draft.save(draftToSave);
                    return id;
                });
            }
        });
    }
    return {
        duplicateRelationshipAndRedirect,
        duplicateDraftFromId,
        duplicateRelationship,
        createStandaloneDraftFromAgreement
    };
};
